import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { TextField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { useForgotPassword } from '@/features/identity/hooks/useForgotPassword';

/** The Forgot Password screen's form — shows the same neutral success state whether or not the
 * account exists, matching the backend's anti-enumeration behavior. */
export function ForgotPasswordForm() {
  const { form, onSubmit, serverError, submitted } = useForgotPassword();
  const { control, formState } = form;

  if (submitted) {
    return (
      <AuthCard>
        <AuthHeader title="Check your email" />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          If an account exists for that email, we&apos;ve sent a link to reset your password.
        </Typography>
        <Button component={RouterLink} to={ROUTES.login} variant="outlined">
          Back to sign in
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      footer={
        <>
          Remembered your password? <RouterLink to={ROUTES.login}>Sign in</RouterLink>
        </>
      }
    >
      <AuthHeader
        title="Forgot your password?"
        subtitle="Enter your email and we'll send you a reset link."
      />
      <Stack component="form" onSubmit={onSubmit} spacing={2} noValidate>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        <TextField name="email" control={control} label="Email" type="email" autoComplete="email" />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={formState.isSubmitting}
          startIcon={
            formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Send reset link
        </Button>
      </Stack>
    </AuthCard>
  );
}
